import React from 'react'
import T from 'prop-types'
import { Box, Grid, Typography } from '@mui/material'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined'
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown'
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import itLocale from 'date-fns/locale/it'
import {
  getFormattedKm,
  getFormattedOptionAmount,
  getFormattedReconditioningEstCost,
  mapLocation,
} from 'lib/carDetailsLib'

import CarDetailsList from './CarDetailsList'
import CarDetailsItem from './CarDetailsItem'
import CarDetailsRightColumn from './CarDetailsRightColumn'
import CarDetailsImageWithCarousel from './CarDetailsImageWithCarousel'
import CarDetailsItemMaintenance from './CarDetailsItemMaintenance'

const CAR_DETAILS_LOCATION_LABEL = 'carDetails.location'

export default function CarDetails({
  car,
  price,
  maxPrice,
  showPriceRange = true,
  pictures = [],
  center,
  rightBottom,
  showAppealing,
  readOnly,
  onToggleFavorite,
  showFavoriteReadOnly = false,
}) {
  const { t } = useTranslation()

  const numberFormatter = new Intl.NumberFormat('it-IT')

  return (
    <Grid container columnSpacing={1}>
      <Grid
        item
        xs={8}
        sx={{ maxHeight: `calc(100vh - 182px)`, overflowY: 'auto', pr: 1 }}
      >
        <div>
          <Box sx={{ pb: 3 }}>
            <CarDetailsImageWithCarousel
              car={car}
              pictures={pictures}
              readOnly={readOnly}
            />
          </Box>
          {center}
          <CarDetailsList title={t('carDetails.specifications')} columns={2}>
            <CarDetailsItem
              title={t('carDetails.km')}
              icon={SpeedOutlinedIcon}
              value={getFormattedKm(car.km)}
            />
            <CarDetailsItem
              title={t('carDetails.fuelType')}
              icon={LocalGasStationOutlinedIcon}
              value={car.fuelType}
            />
            <CarDetailsItem
              title={t(CAR_DETAILS_LOCATION_LABEL)}
              icon={LocationOnOutlinedIcon}
              value={mapLocation(car.location)}
            />
            <CarDetailsItem
              title={t('carDetails.plate')}
              icon={DirectionsCarOutlinedIcon}
              value={car.plate}
            />
            <CarDetailsItem
              title={t('carDetails.transmission')}
              icon={AccountTreeOutlinedIcon}
              value={t(`carDetails.mapping.${car.transmission}`)}
            />
            <CarDetailsItem
              title={t('carDetails.year')}
              icon={CalendarTodayOutlinedIcon}
              value={car.year}
            />
            <CarDetailsItem
              title={t('carDetails.damage')}
              icon={KeyboardDoubleArrowDownIcon}
              value={getFormattedReconditioningEstCost(
                car.reconditioningEstCost
              )}
            />
            <CarDetailsItem
              title={t('carDetails.optionals')}
              icon={DiamondOutlinedIcon}
              value={getFormattedOptionAmount(car.optionAmount)}
            />
          </CarDetailsList>

          <CarDetailsList
            title={t('carDetails.mainCharacteristics')}
            columns={2}
          >
            <CarDetailsItem title={t('carDetails.brand')} value={car.brand} />
            <CarDetailsItem
              title={t('carDetails.km')}
              value={getFormattedKm(car.km)}
            />
            <CarDetailsItem title={t('carDetails.model')} value={car.model} />
            <CarDetailsItem
              title={t('carDetails.transmission')}
              value={t(`carDetails.mapping.${car.transmission}`)}
            />
            <CarDetailsItem title={t('carDetails.plate')} value={car.plate} />
            <CarDetailsItem
              title={t('carDetails.category')}
              value={t(`carDetails.mapping.${car.vehicleCategory}`)}
            />
            <CarDetailsItem
              title={t('carDetails.date')}
              value={format(new Date(car.date), t('common.date.formatDMY'), {
                locale: itLocale,
              })}
            />
            <CarDetailsItem title={t('carDetails.origin')} value={car.origin} />
          </CarDetailsList>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              columnGap: 3,
            }}
          >
            <CarDetailsList title={t('carDetails.motorization')}>
              <CarDetailsItem
                title={t('carDetails.displacement')}
                value={`${numberFormatter.format(car.engineDisplacement)} cm3`}
              />
              <CarDetailsItem
                title={t('carDetails.power')}
                value={`${car.enginePowerKw} kw`}
              />
              <CarDetailsItem
                title={t('carDetails.fourWheelDrive')}
                value={t(`carDetails.mapping.${car.fourWheelDrive}`)}
              />
            </CarDetailsList>

            <CarDetailsList title={t('carDetails.consuption')}>
              <CarDetailsItem
                title={t('carDetails.euroDirective')}
                value={car.euroDirective}
              />
              <CarDetailsItem
                title={t('carDetails.co2Emission')}
                value={`${car.co2Emission} g/kg`}
              />
            </CarDetailsList>

            <CarDetailsList title={t('carDetails.carBody')}>
              <CarDetailsItem title={t('carDetails.type')} value={car.type} />
              <CarDetailsItem
                title={t('carDetails.color')}
                value={car.color || '-'}
              />
              <CarDetailsItem title={t('carDetails.doors')} value={car.doors} />
              <CarDetailsItem title={t('carDetails.seats')} value={car.seats} />
            </CarDetailsList>

            <CarDetailsList title={t(CAR_DETAILS_LOCATION_LABEL)}>
              <CarDetailsItem
                title={t(CAR_DETAILS_LOCATION_LABEL)}
                value={mapLocation(car.location)}
              />
            </CarDetailsList>
          </Box>

          <CarDetailsList title={t('carDetails.equipment')} columns={2}>
            {car.equipment.map((item) => (
              <CarDetailsItem key={item} value={item} />
            ))}
          </CarDetailsList>

          <CarDetailsList title={'Dettaglio manutentivo'} columns={1}>
            {car.maintenance ? (
              <>
                <CarDetailsItemMaintenance
                  key={'header'}
                  title={'Data intervento'}
                  odometer={'Km rilevati'}
                  summary={'Tipo intervento'}
                  titleProps={{ fontWeight: 600 }}
                  odometerProps={{ fontWeight: 600 }}
                  summaryProps={{ fontWeight: 600 }}
                  formatItems={false}
                />
                {car.maintenance.map((item) => (
                  <CarDetailsItemMaintenance
                    key={item.date}
                    title={item.date}
                    odometer={item.mileage}
                    summary={item.activities}
                  />
                ))}
              </>
            ) : (
              <Box fullWidth>
                <Typography
                  component="span"
                  variant="body2"
                  color="textSecondary"
                >
                  Nessun intervento manutentivo
                </Typography>
              </Box>
            )}
          </CarDetailsList>
        </div>
      </Grid>
      <Grid item xs={4}>
        <CarDetailsRightColumn
          car={car}
          price={price}
          maxPrice={maxPrice}
          showPriceRange={showPriceRange}
          showAppealing={showAppealing}
          bottom={rightBottom}
          onToggleFavorite={onToggleFavorite}
          showFavoriteReadOnly={showFavoriteReadOnly}
        />
      </Grid>
    </Grid>
  )
}

CarDetails.propTypes = {
  car: T.object.isRequired,
  price: T.number,
  maxPrice: T.number,
  showPriceRange: T.bool,
  pictures: T.array,
  center: T.node,
  rightBottom: T.node,
  showAppealing: T.bool,
  readOnly: T.bool,
  onToggleFavorite: T.func,
  showFavoriteReadOnly: T.bool,
}
